import _ from 'lodash';
import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Container,
	Empty,
	Input,
	Jumbo,
	Pagination,
	Popover,
	Skeleton,
	Table,
	Tabs,
	tokens,
} from '@treams/treams-ui';

import AIWorkbenchTabs from './AIWorkbenchTabs';
import AnnouncementsSettings from './announcements/AnnouncementsSettings';
import ChatHistory from './ChatHistory';
import ChatHistoryAnalytics from './ChatHistoryAnalytics';
import ChatHistoryDetails from './ChatHistoryDetails';
import LaunchAnalyticsTabs from './LaunchAnalyticsTabs';
import LaunchTreamsScore from './LaunchTreamsScore';
import ModuleSettings from './ModuleSettings';
import PromptInstructions from './PromptInstructions';
import PromptInstructionsDetails from './PromptInstructionsDetails';
import { WorkspaceBasic } from './types';
import WorkspaceCreateModal from './WorkspaceCreateModal';
import WorkspaceDetails from './WorkspaceDetails';

const PlansContainer = styled(Flex)`
	cursor: pointer;
`;

const PlanContainer = styled(Box)<{ expired?: boolean; trial?: boolean }>`
	border: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	border-radius: 3px;
	box-shadow: 0px 1px 2px ${(props) => rgba(props.theme.colors.lightBlueLight, 0.5)};

	&:hover {
		border: 1px solid ${tokens.border.subtle};
	}

	${(props) =>
		props.trial &&
		`
		border-color: ${rgba(props.theme.colors.green, 0.3)};
		color: ${props.theme.colors.greenDark};

		&:hover {
			border-color: ${rgba(props.theme.colors.green, 0.5)};
		}
	`}

	${(props) =>
		props.expired &&
		`
        border-color: ${rgba(props.theme.colors.danger, 0.3)};
        color: ${props.theme.colors.danger};

        &:hover {
            border-color: ${rgba(props.theme.colors.danger, 0.5)};
        }
    `}
`;

function Plans(props: { workspace: WorkspaceBasic }) {
	const { t } = useTranslation('', { keyPrefix: 'workspaces.plans' });

	const plans = _.sortBy(props.workspace.plans, (plan) => {
		return plan.endDate && new Date(plan.endDate).getTime();
	}).reverse();

	return (
		<PlansContainer>
			{plans.map((plan) => (
				<PlanContainer
					key={plan.id}
					p={2}
					mr={1}
					expired={plan.isExpired}
					trial={plan.isTrial}
					flexGrow={1}
				>
					<strong>{t(`type.${_.camelCase(plan.type)}`)}</strong>
				</PlanContainer>
			))}
			{plans.length === 0 && <Box>No subscription</Box>}
		</PlansContainer>
	);
}

function WorkspaceRow({ workspace }: { workspace: WorkspaceBasic }) {
	const { t } = useTranslation();
	const { push } = useHistory();

	return (
		<Table.Row onClick={() => push(`/settings/workspaces/details/${workspace.alias}`)}>
			<Table.Cell basis={0.4}>
				{workspace.id} {` (${workspace.alias})`}
			</Table.Cell>
			<Table.Cell basis={0.3}>
				<Plans workspace={workspace} />
			</Table.Cell>
			<Table.Cell basis={0.3}>
				<Button display="flex" flexGrow={1} type="secondary" title={t('common.edit')} />
			</Table.Cell>
		</Table.Row>
	);
}

function WorkspacesTable(props: { isArchived?: boolean }) {
	const { t } = useTranslation();

	const [prefix, setPrefix] = useState('');
	const [visible, setVisible] = useState(false);
	const [pagination, setPagination] = useState<Pagination>({ first: 10 });

	const query = graphql.useWorkspacesBasicQuery({
		variables: {
			filters: {
				prefix: prefix.toLowerCase(),
				isArchived: props.isArchived ?? false,
			},
			...pagination,
		},
	});

	return (
		<>
			<Flex mb={2} justifyContent="space-between">
				<Flex flexGrow={1}>
					<Input
						icon={<SearchIcon />}
						small
						value={prefix}
						onChange={(value) => {
							setPrefix(value);
							setPagination({ first: 10 });
						}}
						autoFocus
						mr={2}
						placeholder={t('settings.workspaces.search.placeholder')}
					/>
				</Flex>
				<Popover.Target
					backdrop
					center
					contents={<WorkspaceCreateModal onDismiss={() => setVisible(false)} />}
					visible={visible}
				>
					<Button small title={t('common.add')} onClick={() => setVisible(true)} />
				</Popover.Target>
			</Flex>
			<Table>
				<Table.Header>
					<Table.Cell basis={0.4}>Workspace</Table.Cell>
					<Table.Cell basis={0.3}>Subscription</Table.Cell>
					<Table.Cell basis={0.3}>Actions</Table.Cell>
				</Table.Header>
				{query.data ? (
					query.data.workspacesV2.edges.length > 0 ? (
						query.data.workspacesV2.edges
							.filter((e) => {
								return e.node.id !== 'treams';
							})
							.map(({ node }) => <WorkspaceRow key={node.id} workspace={node} />)
					) : (
						<Skeleton.Empty
							contents={
								<Empty
									icon={<SearchIcon />}
									title={t('settings.workspaces.empty.title')}
									description={t('settings.workspaces.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.2} />
									<Table.Cell.Skeleton basis={0.3} />
									<Table.Cell.Skeleton basis={0.3} />
								</Table.Row>
							</Skeleton.Repeat>
						</Skeleton.Empty>
					)
				) : (
					<Skeleton.Repeat count={3}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.2} />
							<Table.Cell.Skeleton basis={0.2} />
							<Table.Cell.Skeleton basis={0.3} />
							<Table.Cell.Skeleton basis={0.3} />
						</Table.Row>
					</Skeleton.Repeat>
				)}
			</Table>
			<Table.Paging pageInfo={query.data?.workspacesV2.pageInfo} onPaginate={setPagination} />
		</>
	);
}

export default function LaunchSettings() {
	const { t } = useTranslation();

	return (
		<Switch>
			<Route path="/settings/workspaces/details/:workspace">
				<WorkspaceDetails />
			</Route>
			<Route path="/settings/workspaces">
				<Jumbo
					title={t('settings.workspaces.title')}
					description={t('settings.workspaces.description')}
					footer={
						<Tabs.Container>
							<Tabs.Item title="Active" path="/settings/workspaces" />
							<Tabs.Item title="Archive" path="/settings/workspaces/archive" />
						</Tabs.Container>
					}
				/>
				<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
					<Switch>
						<Route path="/settings/workspaces/archive">
							<WorkspacesTable isArchived={true} />
						</Route>
						<Route path="/settings/workspaces">
							<WorkspacesTable isArchived={false} />
						</Route>
					</Switch>
				</Container>
			</Route>
			<Route path="/settings/workbench/prompts/:prompt">
				<Jumbo
					title={t('settings.ai-workbench')}
					backButtonLink="/settings/workbench/prompts"
					footer={<AIWorkbenchTabs promptInstructionsExact={false} />}
				/>
				<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
					<PromptInstructionsDetails />
				</Container>
			</Route>
			<Route path="/settings/workbench/history/:workspaceID/:id">
				<Jumbo
					title={t('settings.ai-workbench')}
					backButtonLink="/settings/workbench"
					footer={<AIWorkbenchTabs chatHistoryExact={false} promptInstructionsExact={false} />}
				/>
				<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
					<ChatHistoryDetails />
				</Container>
			</Route>
			<Route path="/settings/workbench">
				<Jumbo
					title={t('settings.ai-workbench')}
					description={t('settings.ai-workbench.description')}
					footer={<AIWorkbenchTabs />}
				/>
				<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
					<Switch>
						<Route path="/settings/workbench/prompts">
							<PromptInstructions />
						</Route>
						<Route path="/settings/workbench/analytics">
							<ChatHistoryAnalytics />
						</Route>
						<Route path="/settings/workbench">
							<ChatHistory />
						</Route>
					</Switch>
				</Container>
			</Route>
			<Route path="/settings/analytics">
				<Jumbo title={t('settings.analytics')} wide={1080} footer={<LaunchAnalyticsTabs />} />
				<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`} wide={1080}>
					<LaunchTreamsScore />
				</Container>
			</Route>
			<Route path="/settings/modules">
				<ModuleSettings />
			</Route>
			<Route path="/settings/announcements">
				<AnnouncementsSettings />
			</Route>
		</Switch>
	);
}
